"use client";

import type { SupSingleVaultV3 } from "@/types";
import React, { useState, useMemo, Fragment } from "react";

import GridCellYieldType from "@/components/Grid/Cell/YieldType";
import UiCopy from "@/components/Ui/Copy";
import UiLabelApy from "@/components/Ui/Label/Apy";
import UiLabelRewardsTag from "@/components/Ui/Label/Rewards/Tag";
import useBlockScanAddress from "@/hooks/useBlockScanAddress";

import moment from "moment";

import ExploitsSection from "@/components/Protocol/About/Table/ExploitsSection";
import DetailsSection from "@/components/Protocol/About/Table/DetailsSection";
import GridCellVaultType from "@/components/Grid/Cell/VaultType";
import FaqSection from "@/components/Protocol/About/Table/FaqSection";
import UiLabelPlaceholder from "@/components/Ui/Label/Placeholder";
import GridCellsLink from "@/components/Grid/Cell/Link";
import { SupListRow, SupListTable, SupText, SupTooltip } from "@repo/core-ui";
import { formatCurrencyCompact, isEmptyAmount, formatUrl } from "@/utils";
import { twMerge } from "tailwind-merge";

interface VaultDetailsTableProps {
  vault: SupSingleVaultV3;
}

type VaultTab = "details" | "metrics" | "fees" | "checks" | "audits" | "exploits" | "faq";
const VaultTabs = ["details", "metrics", "fees", "audits", "exploits", "faq"] as VaultTab[];

export default function VaultDetailsTable({ vault }: VaultDetailsTableProps) {
  const realVaultTabs = useMemo(() => {
    return VaultTabs.filter(tab => {
      switch (tab) {
        default:
          return true;
        case "audits":
          return vault.audits.length > 0;
        case "faq":
          return vault.faq.length > 0;
        case "exploits":
          return vault.exploits.length > 0;
        case "fees":
          return vault.fees.length > 0;
      }
    });
  }, [vault]);
  const convertDate = (time: string) => {
    return new Date(time).toLocaleDateString("en-US");
  };

  const { url: blockChainScanLink } = useBlockScanAddress({
    address: vault.contract_address,
    chain: vault.chain.id,
    type: "address",
  });

  const [selectedTab, setSelectedTab] = useState<VaultTab>("details");
  const hasRewards = (vault.rewards?.length ?? 0) > 0;

  return (
    <>
      <div className="flex shrink-0 items-center justify-center gap-[1px] divide-x divide-neutral-600 border-x-0.5 border-t-0.5 border-neutral-600 font-mono">
        {realVaultTabs.map(tab => (
          <button
            key={`button_${tab}`}
            onClick={() => setSelectedTab(tab)}
            className={twMerge(
              "basis-full py-2 text-xs uppercase leading-[12px] tracking-wider",
              selectedTab === tab ? "bg-neutral text-neutral-900" : "border-b-0.5 border-neutral-600 bg-[transparent]"
            )}
          >
            {tab.toUpperCase()}
          </button>
        ))}
      </div>
      {selectedTab === "details" && (
        <SupListTable className="border-t-0 bg-neutral">
          <SupListRow label="Vault name" value={<SupText variant="subtitle1">{vault.friendly_name}</SupText>} />
          <SupListRow label="Vault Symbol" value={<SupText variant="subtitle1">{vault.symbol}</SupText>} />
          <SupListRow
            label="List date"
            value={
              <SupTooltip unmount portal content="Based on vault listing on Superform date" placement="left" color="primary">
                <div>
                  <SupText variant="subtitle1" className="cursor-help">
                    {moment(vault.created_at).fromNow()}
                  </SupText>
                </div>
              </SupTooltip>
            }
          />
          <SupListRow
            label="Contract Address"
            value={
              <div className="flex space-x-2">
                <GridCellsLink href={blockChainScanLink ?? ""} limit={0} target={"_blank"}>
                  <SupText variant="body1" className="w-full overflow-hidden text-ellipsis whitespace-nowrap normal-case">
                    {vault.contract_address}
                  </SupText>
                </GridCellsLink>
                <UiCopy text={vault.contract_address} />
              </div>
            }
          />
          {vault.yield_type ? <SupListRow label="Yield type" value={<GridCellYieldType vault={vault} />} /> : <Fragment />}

          <SupListRow label="Vault type" value={<GridCellVaultType singleVault={vault} />} />

          {vault.external_url ? (
            <SupListRow
              label="External URL"
              value={
                <GridCellsLink className="max-w-[500px]" href={formatUrl(vault.external_url)} limit={0} target={"_blank"} title={vault.external_url.replace("https://", "")} />
              }
            />
          ) : (
            <Fragment />
          )}

          {vault.has_timelock ? (
            <SupListRow
              label="Timelock"
              value={
                <SupText variant="subtitle1" weight="bold">
                  {vault.timelock_duration}&nbsp;{vault.timelock_unit}
                </SupText>
              }
            />
          ) : (
            <Fragment />
          )}

          <SupListRow label="Last updated" value={<SupText variant="subtitle1">{convertDate(vault.updated_at)}</SupText>} />
        </SupListTable>
      )}
      {selectedTab === "metrics" && (
        <SupListTable className="border-t-0 bg-neutral">
          <SupListRow
            label={
              <SupTooltip color="primary" placement="left" content="Calculated by taking the vault return from the past day and annualizing it with compounding included">
                <span className="cursor-help">24H APY</span>
              </SupTooltip>
            }
            value={
              hasRewards ? (
                <UiLabelRewardsTag isFlexHeight unwrapped iconClass="h-4 w-4 order-last" vault={vault} noAction period="day" />
              ) : (
                <UiLabelApy hideTooltip noAction vault={vault} period="day" />
              )
            }
          />
          <SupListRow
            label={
              <SupTooltip color="primary" placement="left" content="Calculated by taking the vault return from the past 7 days and annualizing it with compounding included">
                <span className="cursor-help">7D APY</span>
              </SupTooltip>
            }
            value={
              hasRewards ? (
                <UiLabelRewardsTag isFlexHeight unwrapped iconClass="h-4 w-4 order-last" vault={vault} noAction period="week" />
              ) : (
                <UiLabelApy hideTooltip noAction vault={vault} period="week" />
              )
            }
          />
          <SupListRow
            label={
              <SupTooltip color="primary" placement="left" content="Calculated by taking the vault return from the past 30 days and annualizing it with compounding included">
                <span className="cursor-help">30D APY</span>
              </SupTooltip>
            }
            value={
              hasRewards ? (
                <UiLabelRewardsTag isFlexHeight unwrapped iconClass="h-4 w-4 order-last" vault={vault} noAction period="month" />
              ) : (
                <UiLabelApy hideTooltip noAction vault={vault} period="month" />
              )
            }
          />
          <SupListRow
            label="TVL current"
            value={
              <SupText variant="subtitle1">
                {
                  <span className="font-mono tracking-[.5px]">
                    {isEmptyAmount(vault.vault_statistics.tvl_now) ? <UiLabelPlaceholder>N/A</UiLabelPlaceholder> : formatCurrencyCompact(vault.vault_statistics.tvl_now)}
                  </span>
                }
              </SupText>
            }
          />
          <SupListRow
            label="TVL daily change"
            value={
              <SupText variant="subtitle1">
                {
                  <span className="font-mono tracking-[.5px]">
                    {isEmptyAmount(vault.vault_statistics.tvl_day_change) ? <UiLabelPlaceholder>N/A</UiLabelPlaceholder> : `${vault.vault_statistics.tvl_day_change}%`}
                  </span>
                }
              </SupText>
            }
          />
          <SupListRow
            label="TVL weekly change"
            value={
              <SupText variant="subtitle1">
                {
                  <span className="font-mono tracking-[.5px]">
                    {isEmptyAmount(vault.vault_statistics.tvl_week_change) ? <UiLabelPlaceholder>N/A</UiLabelPlaceholder> : `${vault.vault_statistics.tvl_week_change}%`}
                  </span>
                }
              </SupText>
            }
          />
          <SupListRow
            label="TVL monthly change"
            value={
              <SupText variant="subtitle1">
                {
                  <span className="font-mono tracking-[.5px]">
                    {isEmptyAmount(vault.vault_statistics.tvl_month_change) ? <UiLabelPlaceholder>N/A</UiLabelPlaceholder> : `${vault.vault_statistics.tvl_month_change}%`}
                  </span>
                }
              </SupText>
            }
          />
        </SupListTable>
      )}
      {selectedTab === "audits" && (
        <div className="border-0.5 border-t-0 border-neutral-600 bg-neutral">
          {Array.isArray(vault.audits) &&
            vault.audits.map((audit, index) => (
              <DetailsSection title={convertDate(audit.audit_date)} linkLabel="View the audit" link={audit.audit_link} key={`audits_${index}`}>
                {audit.audit_name}
              </DetailsSection>
            ))}
        </div>
      )}
      {selectedTab === "exploits" && (
        <div className="border-0.5 border-t-0 border-neutral-600 bg-neutral">
          {Array.isArray(vault.exploits) &&
            vault.exploits.map((exploit, index) => <ExploitsSection title={convertDate(exploit.date)} content={exploit.amount} link={exploit.url} key={`exploits_${index}`} />)}
        </div>
      )}
      {selectedTab === "faq" && (
        <div className="scrollbar-table min-w-0 flex-1 overflow-y-auto border-0.5 border-t-0 border-neutral-600 bg-neutral">
          {Array.isArray(vault.faq) && vault.faq.map((faq, index) => <FaqSection title={faq.question} content={faq.answer} key={`faq_${index}`} />)}
        </div>
      )}
      {selectedTab === "fees" && (
        <SupListTable className="border-t-0 bg-neutral">
          {Array.isArray(vault.fees) ? (
            vault.fees.map((fee, index: number) => (
              <SupListRow
                label={fee.fee_name}
                key={`faq_${index}`}
                value={
                  <SupText variant="subtitle1">
                    <span className="flex w-full items-center justify-between">
                      <span className="sm-[ml-4] ml-1 mr-2 flex items-center bg-warning-100 p-1 text-sm">{fee.fee_amount}%</span>
                      <span>{fee.fee_description}</span>
                    </span>
                  </SupText>
                }
              />
            ))
          ) : (
            <Fragment />
          )}
        </SupListTable>
      )}
    </>
  );
}
